import React, { useState, useEffect } from "react";
import { ShoppingBag, Check, X, Eye } from "lucide-react";
import { products as mockProducts } from "../mock/mock";
import { useToast } from "../hooks/use-toast";
import { getProducts, downloadUrl } from "../lib/api";

export default function Shop() {
  const [products, setProducts] = useState(mockProducts);
  const [cart, setCart] = useState([]);
  const [preview, setPreview] = useState(null);
  const { toast } = useToast();

  useEffect(() => {
    getProducts()
      .then((data) => { if (Array.isArray(data) && data.length) setProducts(data); })
      .catch(() => {});
  }, []);

  const add = (p) => {
    if (p.price === 0) {
      window.open(downloadUrl(p.id), "_blank");
      toast({ title: "Downloading…", description: `${p.name} (PDF) is on its way.` });
      return;
    }
    setCart((c) => (c.includes(p.id) ? c : [...c, p.id]));
    toast({ title: "Added to cart", description: `${p.name} — $${p.price.toFixed(2)}` });
  };

  return (
    <div>
      <section className="max-w-4xl mx-auto px-5 pt-16 pb-8 text-center">
        <p className="font-ui tracking-[0.3em] text-teal text-sm font-semibold uppercase">The Shop</p>
        <h1 className="font-serif-display text-5xl md:text-6xl font-bold text-navy mt-4">Workbooks &amp; Digital Downloads</h1>
        <p className="font-ui text-navy/70 mt-5 text-lg leading-relaxed max-w-2xl mx-auto">
          For small business owners who are done winging it. Practical, honest tools you can start using today —
          delivered instantly, yours for life.
        </p>
        {cart.length > 0 && (
          <div className="mt-6 inline-flex items-center gap-2 bg-navy text-cream px-5 py-2.5 rounded-full font-ui text-sm">
            <ShoppingBag size={16} /> {cart.length} item{cart.length > 1 ? "s" : ""} in cart
          </div>
        )}
      </section>

      <section className="max-w-6xl mx-auto px-5 py-10">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {products.map((p) => {
            const inCart = cart.includes(p.id);
            const free = p.price === 0;
            return (
              <div key={p.id} className="lift bg-white rounded-2xl overflow-hidden border flex flex-col" style={{ borderColor: "var(--cream-dark)" }}>
                <div className="relative h-52 overflow-hidden group">
                  <img src={p.img} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <span className="absolute top-3 left-3 font-ui text-xs font-semibold px-3 py-1 rounded-full bg-cream/95 text-navy">{p.category}</span>
                  {free && <span className="absolute top-3 right-3 font-ui text-xs font-bold px-3 py-1 rounded-full bg-teal text-navy">FREE</span>}
                  <button onClick={() => setPreview(p)} className="absolute bottom-3 right-3 w-9 h-9 rounded-full bg-cream/95 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white" title="Quick preview">
                    <Eye size={16} className="text-navy" />
                  </button>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="font-serif-display text-xl font-semibold text-navy leading-tight">{p.name}</h3>
                  <p className="font-ui text-sm text-navy/70 mt-2 leading-relaxed flex-1">{p.desc}</p>
                  <p className="font-ui text-xs text-teal mt-3">{p.preview}</p>
                  <div className="flex items-center justify-between mt-5">
                    <span className="font-serif-display text-2xl font-bold text-navy">{free ? "Free" : `$${p.price.toFixed(2)}`}</span>
                    <button
                      onClick={() => add(p)}
                      className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-ui text-sm font-semibold transition-colors ${inCart ? "bg-teal text-navy" : "bg-navy text-cream hover:bg-orange"}`}
                    >
                      {inCart ? <><Check size={16} /> Added</> : free ? "Download" : "Add to Cart"}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Preview modal */}
      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-5 bg-navy/60 backdrop-blur-sm" onClick={() => setPreview(null)}>
          <div className="bg-white rounded-2xl max-w-lg w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="relative h-56">
              <img src={preview.img} alt={preview.name} className="w-full h-full object-cover" />
              <button onClick={() => setPreview(null)} className="absolute top-3 right-3 w-9 h-9 rounded-full bg-cream/95 flex items-center justify-center hover:bg-white">
                <X size={18} className="text-navy" />
              </button>
            </div>
            <div className="p-7">
              <span className="font-ui text-xs font-semibold text-teal uppercase tracking-wider">{preview.category}</span>
              <h3 className="font-serif-display text-2xl font-bold text-navy mt-1">{preview.name}</h3>
              <p className="font-ui text-navy/75 mt-3 leading-relaxed">{preview.desc}</p>
              <p className="font-ui text-sm text-teal mt-4">{preview.preview}</p>
              <div className="flex items-center justify-between mt-6">
                <span className="font-serif-display text-3xl font-bold text-navy">{preview.price === 0 ? "Free" : `$${preview.price.toFixed(2)}`}</span>
                <button onClick={() => { add(preview); setPreview(null); }} className="bg-navy text-cream px-6 py-3 rounded-full font-ui font-semibold hover:bg-orange transition-colors">
                  {preview.price === 0 ? "Download" : "Add to Cart"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
